import { Footer, FooterLite, Header } from '@aspendental/shared-components-web';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';

import FurtherAssistanceContact from '@src/components/FurtherAssistanceContact';
import GlobalDialog from '@src/components/GlobalDialog';
import GlobalLoadingSpinner from '@src/components/GlobalLoadingSpinner';
import { SITE_META_DESCRIPTIONS } from '@src/constants';
import { AdFooter, App, Maybe } from '@src/lib/__generated/sdk';
import { useAppStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';

interface LayoutPropsInterface {
	children: ReactNode;
	appData: Maybe<App>;
	footerData: Maybe<AdFooter>;
	isFooterLite?: boolean;
	withSimpleLayout?: boolean;
	locale: string;
	preview: boolean;
	logoHref: string;
}

export const Layout = ({
	appData,
	footerData,
	isFooterLite = true,
	withSimpleLayout = false,
	children,
	logoHref,
}: LayoutPropsInterface) => {
	const [isClient, setIsClient] = useState(false);
	const { mottoFlow, languageToggleEnabled } = useAppStore((store) => store);
	const { hasReasonForVisit } = useReasonForVisitStore((store) => store);
	const geoLocation = usePatientInformationFormStore((store) => store.geoLocation);
	const { latitude, longitude } = geoLocation;
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
	const { pathname } = useRouter();

	const isOnPatientServicesApp = pathname.includes('/patient-services');
	const isOnPatientServicesLogin = pathname === '/patient-services';
	const isOnPatientServicesAuthentication = pathname.includes('/patient-services/authentication');
	const isOnPatientServicesMagicLinkExpiration = pathname.includes('/patient-services/expiration');
	const isOnPatientServicesAppointment = pathname.includes('/patient-services/appointment');

	const mainBgColor = isOnPatientServicesApp
		? {
				xs: isOnPatientServicesAppointment
					? 'backgrounds.neutral'
					: isOnPatientServicesLogin ||
					  isOnPatientServicesAuthentication ||
					  isOnPatientServicesMagicLinkExpiration
					? 'backgrounds.default'
					: 'backgrounds.default',
				sm: isOnPatientServicesAppointment
					? 'backgrounds.neutral'
					: isOnPatientServicesLogin ||
					  isOnPatientServicesAuthentication ||
					  isOnPatientServicesMagicLinkExpiration
					? 'backgrounds.default'
					: 'backgrounds.neutral',
		  }
		: {
				xs: 'backgrounds.default',
				sm:
					(hasReasonForVisit && !mottoFlow) || (mottoFlow && latitude + longitude !== 0)
						? 'backgrounds.neutral'
						: 'backgrounds.default',
		  };

	const needFurtherAssistanceBgColor = isOnPatientServicesApp
		? {
				xs: isOnPatientServicesAppointment
					? 'backgrounds.neutral'
					: isOnPatientServicesLogin ||
					  isOnPatientServicesAuthentication ||
					  isOnPatientServicesMagicLinkExpiration
					? 'backgrounds.default'
					: 'backgrounds.default',
				sm: isOnPatientServicesAppointment
					? 'backgrounds.neutral'
					: isOnPatientServicesLogin ||
					  isOnPatientServicesAuthentication ||
					  isOnPatientServicesMagicLinkExpiration
					? 'backgrounds.default'
					: 'backgrounds.neutral',
		  }
		: undefined;

	useEffect(() => {
		setIsClient(true);
		const scopedBaseline = document.getElementById('mui-scoped-baseline');
		if (scopedBaseline) {
			scopedBaseline.style.height = '100vh';
		}
	}, []);

	// unset overflowX for sticky button on mobile
	useEffect(() => {
		const scopedBaseline = document.getElementById('mui-scoped-baseline');
		if (scopedBaseline?.style && isSmallScreen) {
			scopedBaseline.style.overflowX = 'unset';
		}
	}, [isSmallScreen]);

	if (!isClient) {
		return null;
	}

	const siteDescription = mottoFlow ? SITE_META_DESCRIPTIONS.motto : SITE_META_DESCRIPTIONS.standard;

	// header height options do not include exact specs given for scheduling app, overwrite css to avoid polluting shared component
	const desktopHeaderOverwrites = {
		'header[data-test-id="section_header_desktop"]': { height: '5.5rem' },
		'header > div.MuiToolbar-root': { padding: '0 5%' },
	};

	return (
		<>
			<Head>
				<title>Schedule an Appointment | Aspen Dental</title>
				<meta name="description" content={siteDescription} data-react-helmet="true" />
			</Head>
			<Box display={'flex'} flexDirection={'column'} minHeight={'100vh'}>
				{appData && (
					<Box sx={{ ...desktopHeaderOverwrites, ...(isMediumScreen && { height: '5.5rem' }) }}>
						<Header
							appData={appData}
							withMenuCTA={false}
							hideLocation={true}
							hidePrimaryActionButton={true}
							logoHref={logoHref}
							showLanguageToggle={languageToggleEnabled}
							useHeaderLocationCards={false}
						/>
					</Box>
				)}
				<Box
					sx={{
						bgcolor: mainBgColor,
					}}
				>
					{children}
				</Box>
				{isSmallScreen || (!hasReasonForVisit && !mottoFlow) || (mottoFlow && latitude + longitude === 0) ? (
					<Grid
						sx={{
							bgcolor: needFurtherAssistanceBgColor,
						}}
						id="need-further-assistance"
						pt={2}
						pb={7.5}
						container
						justifyContent="center"
					>
						<FurtherAssistanceContact />
					</Grid>
				) : null}
				{footerData && isFooterLite ? (
					<FooterLite footerData={footerData} />
				) : (
					<Footer footerData={footerData} withScheduling={withSimpleLayout} />
				)}
				<GlobalDialog />
				<GlobalLoadingSpinner />
				<Box sx={{ flexGrow: 1 }} bgcolor="primary.main" />
			</Box>
		</>
	);
};
